import { useState } from 'react';
import { Link } from 'react-router-dom';

import Header from '../components/Header';
import Footer from '../components/Footer';

// ============================================================
// PRÉ-LANÇAMENTO — Landing Page
// CTA leva pra /registro (cadastro existente)
// Após cadastrar → email confirmação → login → /lista-espera
// ============================================================

const steps = [
  {
    title: "Envie sua planta baixa",
    description: "Faça upload do PDF ou imagem da planta do seu projeto.",
    icon: (
      <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    ),
  },
  {
    title: "Nossa IA analisa tudo automaticamente",
    description: "Cômodos, áreas, esquadrias, acabamentos e quantitativos de materiais — tudo identificado em minutos.",
    icon: (
      <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: "Receba o orçamento completo",
    description: "Orçamento detalhado com quantitativo de materiais completo. Exporte em PDF ou Excel profissional.",
    icon: (
      <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
];

const features = [
  {
    title: "Leitura de planta com IA",
    description: "Única ferramenta brasileira que lê sua planta baixa automaticamente com Inteligência Artificial.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
  },
  {
    title: "Baseado no CUB e SINAPI oficiais",
    description: "Dados atualizados dos Sinduscon estaduais e do sistema SINAPI da Caixa para máxima confiabilidade.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: "Exporta PDF e Excel",
    description: "Relatórios profissionais prontos para apresentar ao cliente ou ao banco.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    title: "Economia de tempo",
    description: "Reduza em até 80% o tempo gasto com levantamento de quantitativos e orçamentação manual.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const audiences = [
  {
    emoji: "👷",
    title: "Engenheiros e Arquitetos",
    description: "Automatize a parte mais trabalhosa do orçamento. Foque no que realmente importa — seus projetos.",
  },
  {
    emoji: "🏠",
    title: "Quem vai construir",
    description: "Descubra quanto vai custar sua obra antes de começar. Sem surpresas no meio da construção.",
  },
  {
    emoji: "📋",
    title: "Orçamentistas",
    description: "Ganhe velocidade nos seus levantamentos. Reduza o tempo de quantitativos em até 80%.",
  },
];

const faqs = [
  {
    question: "Quando o QuantoCustaObra vai estar disponível?",
    answer: "Estamos na fase final de testes e calibração do motor de IA. Cadastre-se para ser avisado assim que lançarmos — os primeiros cadastrados terão acesso prioritário!",
  },
  {
    question: "Como funciona a leitura de planta com IA?",
    answer: "Você faz upload do PDF ou imagem da planta baixa. Nossa Inteligência Artificial identifica automaticamente os cômodos, áreas, esquadrias e acabamentos, e calcula todos os quantitativos de materiais.",
  },
  {
    question: "O orçamento é confiável?",
    answer: "Sim! Utilizamos o CUB oficial dos Sinduscon estaduais e composições SINAPI da Caixa Econômica Federal, atualizados mensalmente. O motor de IA é calibrado com plantas reais brasileiras e validado por engenheira especialista em orçamentação.",
  },
  {
    question: "Funciona para qualquer tipo de planta?",
    answer: "Funciona para plantas baixas residenciais em formato PDF ou imagem (PNG, JPG). O motor lê plantas de casas térreas e sobrados, incluindo projetos MCMV. Prédios e plantas industriais serão suportados em breve.",
  },
  {
    question: "Preciso ter conhecimento técnico?",
    answer: "Não! O sistema foi pensado para ser simples. Basta fazer o upload da planta e a IA cuida do resto. Mas profissionais da construção também se beneficiam com a agilidade e precisão.",
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-card rounded-xl px-6 shadow-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left font-display font-semibold text-secondary cursor-pointer"
      >
        {question}
        <svg
          className={`w-5 h-5 text-muted-foreground flex-shrink-0 ml-4 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="text-muted-foreground leading-relaxed pb-5">
          {answer}
        </div>
      )}
    </div>
  );
}

function PreLaunchLanding() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-muted/30" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent" />

        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
                🚀 Em breve — Pré-lançamento
              </div>

              <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-tight text-secondary mb-6">
                Da Planta Baixa ao{' '}
                <span className="text-primary">Orçamento Completo</span>{' '}
                em Minutos
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl leading-relaxed">
                O QuantoCustaObra usa Inteligência Artificial para ler sua planta,
                identificar cômodos, calcular materiais e gerar um orçamento
                detalhado — automaticamente.
              </p>

              <Link to="/registro" className="no-underline">
                <button className="btn-cta text-lg px-8 py-4 mb-8 animate-pulse-glow">
                  Cadastre-se para Acesso Antecipado
                  <svg className="w-5 h-5 ml-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </Link>

              <div className="flex flex-col sm:flex-row gap-4">
                {[
                  "Leitura automática de planta com IA",
                  "Orçamento completo + quantitativo de materiais",
                  "Exportação em PDF e Excel"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bruno mascot */}
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/10 rounded-full blur-3xl" />
                <img
                  src="/images/brand/bruno-mascot.png"
                  alt="Bruno, mascote engenheiro do QuantoCustaObra"
                  className="relative w-80 h-auto animate-float"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-secondary mb-4">Como Funciona?</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Três passos simples para ter seu orçamento completo</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.title} className="relative text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">{step.icon}</div>
                <span className="inline-flex w-8 h-8 rounded-full bg-primary text-white text-sm font-bold items-center justify-center mx-auto mb-4">{i + 1}</span>
                <h3 className="font-display font-bold text-xl text-secondary mb-3">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POR QUE USAR */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-secondary mb-4">Por que usar o QuantoCustaObra?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {features.map((f) => (
              <div key={f.title} className="bg-card rounded-xl p-6 shadow-card hover:shadow-elevated transition-shadow duration-300">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">{f.icon}</div>
                <h3 className="font-display font-bold text-lg text-secondary mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARA QUEM */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-secondary mb-4">Para Quem é o QCO?</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {audiences.map((a) => (
              <div key={a.title} className="text-center p-8">
                <div className="text-5xl mb-4">{a.emoji}</div>
                <h3 className="font-display font-bold text-xl text-secondary mb-3">{a.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{a.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-gradient-stats">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { value: "27", label: "Estados cobertos" },
              { value: "98%", label: "Margem de precisão" },
              { value: "~5min", label: "Tempo de análise" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-display font-extrabold text-4xl md:text-5xl text-white mb-2">{stat.value}</div>
                <div className="text-white/70 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-secondary mb-4">Perguntas Frequentes</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-secondary mb-4">Não perca o lançamento!</h2>
          <p className="text-muted-foreground text-lg mb-8">
            Cadastre-se agora e seja um dos primeiros a revolucionar
            seus orçamentos de obra com Inteligência Artificial.
          </p>
          <Link to="/registro" className="no-underline">
            <button className="btn-cta text-lg px-8 py-4 animate-pulse-glow">
              Cadastre-se Agora
              <svg className="w-5 h-5 ml-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </Link>
        </div>
      </section>

      {/* FLOATING CTA - Mobile */}
      <div className="fixed bottom-4 left-4 right-4 z-40 md:hidden">
        <Link to="/registro" className="no-underline">
          <button className="btn-cta w-full text-base px-6 py-3.5 shadow-elevated flex items-center justify-center gap-2">
            🚀 Cadastre-se para Acesso Antecipado
          </button>
        </Link>
      </div>

      <Footer />
    </div>
  );
}

export default PreLaunchLanding;
